import React from 'react'
import styled, { css, FlattenSimpleInterpolation } from 'styled-components'

import { useMedia } from '@/utils/use-media'
import heartEyesImg from '@/assets/images/hearteyes.gif'
import placeholder1 from '@/assets/images/why_1.gif'
import placeholder2 from '@/assets/images/why_2.gif'
import placeholder3 from '@/assets/images/why_3.gif'
import placeholder4 from '@/assets/images/why_4.gif'
import { GradientLink } from '@/components/ui/gradient-link'
import { mobile } from '@//utils/media-query'

const blockRowWidth = 320

const ValueGif = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  img {
    width: 100px;
    @media ${mobile.query} {
      width: 60px;
    }
  }

  h1 {
    margin-bottom: 15px;
  }
`

const Blurb = styled.div`
  text-align: center;
  max-width: 1220px;
  margin-bottom: 75px;

  p {
    opacity: 1;
    margin-bottom: 0;
    font-size: 22px;
    line-height: 30px;
  }

  @media ${mobile.query} {
    box-sizing: border-box;
    padding: 0 15px;
    width: 100%;

    p {
      font-size: 18px;
      line-height: 26px;
      margin-bottom: -34px;
      opacity: 0.85;
    }
  }
`

const Blocks = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`

type BlockRowProps = {
  isReverse: boolean
}

const BlockRow = styled.div<BlockRowProps>`
  display: flex;
  flex-direction: ${({ isReverse }) => (isReverse ? 'row-reverse' : 'row')};
  margin-bottom: 30px;

  ${({ isReverse }) =>
    isReverse
      ? css`
          @media ${mobile.query} {
            overflow: hidden;
            flex-direction: column;
            align-self: flex-start;
            margin: 20px 0;
          }
        `
      : css`
          @media ${mobile.query} {
            overflow: hidden;
            flex-direction: column;
            align-self: flex-end;
            align-items: flex-end;
            text-align: right;
            margin: 20px 0;
          }
        `}
`

const BlockImage = styled.div<{ mobileStyles?: FlattenSimpleInterpolation }>`
  flex-basis: 50%;
  position: relative;
  height: ${({ theme }) => theme.blockTextHeight}px;

  img {
    position: absolute;
    top: 0;
    height: auto;
    width: 672px;

    @media ${mobile.query} {
      height: 203px;
      position: unset;
      width: auto;
    }
  }

  @media ${mobile.query} {
    height: auto;
    overflow: hidden;
    ${({ mobileStyles }) => mobileStyles}
  }
`

type BlockTextProps = {
  isHorizEnd?: boolean
}

const BlockText = styled.div<BlockTextProps>`
  display: flex;
  justify-content: center;
  align-items: ${({ isHorizEnd }) => (isHorizEnd ? 'flex-end' : 'flex-start')};
  flex-direction: column;
  flex-basis: 50%;
  height: ${({ theme }) => theme.blockTextHeight}px;

  ${({ isHorizEnd }) => (isHorizEnd ? 'padding-right: 16px' : 'padding-left: 16px')};

  @media ${mobile.query} {
    height: auto;
    padding: 25px 14px 0;
  }
`

type InfoProps = BlockRowProps

const Info = styled.div<InfoProps>`
  display: flex;
  flex-direction: column;
  width: ${blockRowWidth}px;

  ${({ isReverse }) =>
    isReverse &&
    css`
      @media ${mobile.query} {
        align-items: flex-end;
      }
    `}

  @media ${mobile.query} {
    width: 100%;
  }
`

const Title = styled.h3`
  margin: 0 0 15px;
  width: 100%;
  line-height: 30px;
`

const rows = [
  {
    title: 'The Most Engaging Content',
    text: `Our exclusive clip, gif, and sticker library is powered by thousands of artists and hundreds of premium content partners. We a directly with them so you can feel secure knowing it's safe for distribution everywhere your app is used.`,
    linkText: 'Open in Docs',
    url: '/docs/sdk#content-types',
    src: placeholder1,
    isReverse: true,
    mobileOffset: '0px',
  },
  {
    title: `Powerful Search Algorithms`,
    text: `Our unparalleled search algorithm lets users easily express themselves and animate their conversations with the exact GIF they we’re looking for, or didn’t know they wanted. It also uses analytics to improve results and keep users coming back for more.`,
    linkText: 'Open in Docs',
    url: '/docs/api/endpoint#search',
    src: placeholder2,
    isReverse: false,
    mobileOffset: '-12px',
  },
  {
    title: 'Optimized Loading & Renditions',
    text: `We offer extensive rendition options for different platforms and devices to ensure loading content doesn’t get in the way of your app’s speed. Better yet, it’s already built directly into our SDK.`,
    linkText: 'Open in Docs',
    url: '/docs/optional-settings/#rendition-guide',
    src: placeholder3,
    isReverse: true,
    mobileOffset: '-12px',
  },
  {
    title: 'Want even more customization?',
    text: `Select partners can access the GIPHY APIs to power completely bespoke experiences.`,
    linkText: 'Open in Docs',
    url: '/docs/api#quick-start-guide',
    src: placeholder4,
    isReverse: false,
    mobileOffset: '-65px',
    mobileStyles: css`
      img {
        transform: scale(2.5) translateX(-20px);
      }
    `,
  },
]

export const WhyGiphySection = () => {
  const isMobileScreen = useMedia(mobile.query, false)

  return (
    <section>
      <ValueGif>
        <img alt="" src={heartEyesImg} />
        <h1>Why GIPHY?</h1>
      </ValueGif>
      <Blurb>
        <p>
          As the first and largest GIF search engine that serves more than 10 billion GIFs per day,
          it’s safe to say GIPHY knows GIFs. We’ve built powerful SDK and API tools and an extensive
          library of GIFs and Clips so you can access the best reaction search engine in the game.
        </p>
      </Blurb>
      <Blocks>
        {rows.map(
          ({ title, text, linkText, url, src, isReverse, mobileOffset, mobileStyles }, index) => (
            <BlockRow isReverse={isReverse} key={index}>
              <BlockImage
                mobileStyles={mobileStyles}
                style={
                  isReverse
                    ? { marginLeft: isMobileScreen ? mobileOffset : 0 }
                    : { marginRight: isMobileScreen ? mobileOffset : 0 }
                }
              >
                <img alt="" style={isReverse ? { left: 0 } : { right: 0 }} src={src} />
              </BlockImage>
              <BlockText isHorizEnd={!isReverse}>
                <Info isReverse={!isReverse}>
                  <Title>{title}</Title>
                  <p>{text}</p>
                  <GradientLink text={linkText} url={url} />
                </Info>
              </BlockText>
            </BlockRow>
          )
        )}
      </Blocks>
    </section>
  )
}
